/**
 * Curated seeds for the offline clue store (issue #94, NFR-6.5).
 *
 * The offline batch generator (`generate-clue-store.ts`, an operator-run script) walks
 * `CURATED_CATEGORY_SEEDS × CLUE_STORE_AGE_BANDS`, feeding chunks of category names to
 * the existing Strands generator and harvesting **every** produced column (both board
 * rounds + the Final it generates) as a validated, self-revealing-clean reusable
 * `CategoryInstance` (D94.3/D94.4). Online play then assembles boards from stored
 * instances for covered categories, falling back to live generation otherwise
 * (D94.6/D94.11).
 *
 * It lives in `shared` (not `game-server`) as a plain constant — no wire type, no
 * `PROTOCOL_VERSION` impact (D94.10) — so the batch script, tests, and the store-aware
 * lobby suggestions (D94.14) all agree on one source of truth. It extends the concept of
 * the #37 lobby cold-start seeds (`COLD_START_SEEDS` in the game-server suggester); keep
 * the two roughly aligned when either grows.
 *
 * Bounded on purpose (D94.4): ~60 categories × 3 bands is a few hundred generated
 * columns, which bounds initial batch spend. Grow the list over time; an uncovered
 * category always degrades to the live path, so it never needs to be exhaustive.
 */

import type { AgeBand } from './domain';

/** The age bands the store pre-generates for (D94.9 lookup bands). */
export const CLUE_STORE_AGE_BANDS: readonly AgeBand[] = ['KID', 'TEEN', 'ADULT'] as const;

/**
 * Curated board-category names to pre-generate. Broad, evergreen, family-safe topics
 * that read well across age bands (the generator tunes difficulty per band). Ordered
 * roughly by breadth; deterministic — no clock/RNG.
 */
export const CURATED_CATEGORY_SEEDS: readonly string[] = [
  'World Capitals',
  'World Geography',
  'U.S. Presidents',
  'American History',
  'World History',
  'Ancient Civilizations',
  'Famous Explorers',
  'Space & Astronomy',
  'The Human Body',
  'Animals',
  'Dinosaurs',
  'Ocean Life',
  'Weather & Climate',
  'Chemistry',
  'Physics',
  'Biology',
  'Inventions',
  'Technology',
  'Mathematics',
  'Famous Scientists',
  'Classic Literature',
  'Children’s Books',
  'Shakespeare',
  'Poetry',
  'Mythology',
  'World Religions',
  'Languages & Words',
  'Art & Artists',
  'Classical Music',
  'Musical Instruments',
  'Broadway & Musicals',
  'Movies',
  'Animated Films',
  'Television',
  'Video Games',
  'Comic Book Heroes',
  'Board Games',
  'Sports',
  'The Olympics',
  'Baseball',
  'Basketball',
  'Soccer',
  'Food & Cooking',
  'Fruits & Vegetables',
  'World Cuisine',
  'Famous Landmarks',
  'Rivers & Lakes',
  'Mountains',
  'National Parks',
  'Countries of the World',
  'Flags of the World',
  'Currencies',
  'Transportation',
  'Cars & Automobiles',
  'Trains & Railroads',
  'Aviation',
  'Architecture',
  'Fashion',
  'Holidays & Traditions',
  'Nature & Plants',
];
